import { useCallback, useRef } from 'react';
import { InteractionType, RedirectRequest } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from "@azure/msal-react";

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

/**
 * Custom hook to call a web API using bearer token obtained from MSAL
 * @param {RedirectRequest} msalRequest 
 * @returns 
 */

interface UseFetchWithMsalReturn {
    execute: <T>(method: Method, endpoint: string, data?: any) => Promise<T | null>;
    isReady: boolean;
}

const useFetchWithMsal = (msalRequest: RedirectRequest): UseFetchWithMsalReturn => {
    const { instance } = useMsal();

    // Stabilize msalRequest so it doesn't cause execute to re-create on every render
    const msalRequestRef = useRef(msalRequest);

    const { result, error: msalError } = useMsalAuthentication(InteractionType.Redirect, {
        ...msalRequestRef.current,
        account: instance.getActiveAccount() ?? undefined,
    });

    // Keep a ref so execute always reads the latest result/error without
    // needing to be re-created on every render.
    const resultRef = useRef(result);
    resultRef.current = result;
    const msalErrorRef = useRef(msalError);
    msalErrorRef.current = msalError;

    /**
     * Execute a fetch request with the given options
     * @param {string} method: GET, POST, PUT, DELETE
     * @param {String} endpoint: The endpoint to call
     * @param {Object} data: The data to send to the endpoint, if any 
     * @returns JSON response
     */
    const execute = useCallback(async <T>(method: Method, endpoint: string, data: any | null = null): Promise<T | null> => {
        if (msalErrorRef.current) {
            console.info(msalErrorRef.current);
            throw new Error(JSON.stringify(msalErrorRef.current));
        }

        // Try the cached result first; if missing, do a silent acquireToken so
        // mutations (POST/DELETE) that fire after initial load still get a token.
        let accessToken = resultRef.current?.accessToken;
        if (!accessToken) {
            try {
                const account = instance.getActiveAccount() ?? undefined;
                const tokenResult = await instance.acquireTokenSilent({
                    ...msalRequestRef.current,
                    account,
                });
                accessToken = tokenResult.accessToken;
            } catch {
                // Silent refresh failed � redirect flow will kick in automatically
                return null;
            }
        }

        const headers = new Headers();
        headers.append("Authorization", `Bearer ${accessToken}`);
        if (data) headers.append('Content-Type', 'application/json');

        const options: RequestInit = {
            method: method,
            headers: headers,
            body: data ? JSON.stringify(data) : null,
        };

        const response: Response = await fetch(endpoint, options);

        if (response.status !== 200) {
            return null as T;
        }

        const responseData: any = await response.json();
        return responseData as T;
    }, [instance]);

    return {
        execute,
        isReady: !!result,
    };
};

export default useFetchWithMsal;
